import React, { useState } from 'react';
import { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useExportPDF } from '../hooks/reactQuerys/DocumentQueries';
import { useAlert } from '../context/AlertContext';
import {
  downloadBase64,
  useExportLegalTransaction,
  useLegalTransactionExportFormats,
} from '../hooks/reactQuerys/ExportQueries';

interface IProps {
  legalTransaction: ILegalTransactionFullObject;
  /** True wenn der aktuelle Vorgang ungespeicherte Aenderungen hat. */
  hasUnsavedChanges?: boolean;
  /** Wird vom Parent gesetzt: speichert den aktuellen Vorgang.
   *  Wenn dieser Promise resolved, ist der gespeicherte Zustand auf dem
   *  Server — der Export hat damit alle bisher hochgeladenen Dokumente
   *  und Aenderungen mit drin. */
  onSaveBeforeExport?: () => Promise<void>;
}

export const LegalTransactionExport: React.FC<IProps> = ({
  legalTransaction,
  hasUnsavedChanges = false,
  onSaveBeforeExport,
}) => {
  const { showAlert } = useAlert();

  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);
  const [isSavingBeforeExport, setIsSavingBeforeExport] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(1);
  const [xjustizVersion, setXjustizVersion] = useState<string | undefined>(
    undefined
  );
  const [sdsAreaOfLaw, setSdsAreaOfLaw] = useState<string>('');
  // Nur fuer SDS + XJustiz relevant: Dokumente (Urkunden, Entwuerfe, ...)
  // als ZIP-Bundle mitexportieren.
  const [includeDocuments, setIncludeDocuments] = useState<boolean>(true);

  const { exportPDF, isPendingExportPDF } = useExportPDF();
  const { exportLegalTransaction: exportFormat, isExporting } =
    useExportLegalTransaction();
  const { formats } = useLegalTransactionExportFormats();
  const xjustizFormat = formats.find(f => f.key === 'xjustiz');
  const sdsFormat = formats.find(f => f.key === 'strukturdatensatz');

  // Default-Version setzen, sobald die Format-Liste geladen ist.
  if (xjustizFormat?.defaultVersion && xjustizVersion === undefined) {
    setXjustizVersion(xjustizFormat.defaultVersion);
  }

  const onClose = () => {
    setIsOpenDialog(false);
    setSelectedMenu(1);
  };

  // Beim Klick auf den Export-Button entscheiden wir, ob direkt der
  // Format-Dialog auf geht oder vorher der Save-Prompt — analog zum
  // bestehenden Speichern-und-Senden-Pattern.
  const handleExportButtonClick = () => {
    if (hasUnsavedChanges && onSaveBeforeExport) {
      setUnsavedConfirmOpen(true);
    } else {
      setIsOpenDialog(true);
    }
  };

  const handleSaveAndContinue = async () => {
    if (!onSaveBeforeExport) {
      setUnsavedConfirmOpen(false);
      setIsOpenDialog(true);
      return;
    }
    setIsSavingBeforeExport(true);
    try {
      await onSaveBeforeExport();
      setUnsavedConfirmOpen(false);
      setIsOpenDialog(true);
    } catch (e) {
      showAlert({
        severity: 'error',
        text: 'Speichern fehlgeschlagen — Export abgebrochen.',
      });
    } finally {
      setIsSavingBeforeExport(false);
    }
  };

  const handleSkipSaveAndContinue = () => {
    setUnsavedConfirmOpen(false);
    setIsOpenDialog(true);
  };

  const isPending = isPendingExportPDF || isExporting;

  const getUploadObject = () => {
    return {
      ...legalTransaction,
      UserInformed_LackOfActivity: Boolean(
        legalTransaction.UserInformed_LackOfActivity
      ),
      syncWithArnoTop: Boolean(legalTransaction.syncWithArnoTop),
      // Echtes Boolean senden — MySQL liefert das tinyint als 0/1, der
      // Swagger-Validator akzeptiert aber nur boolean/null.
      RequestClientMeeting: Boolean(legalTransaction.RequestClientMeeting),
      RequestClientData: Boolean(legalTransaction.RequestClientData),
    } as ILegalTransactionFullObject;
  };

  const handleExportPdf = async () => {
    if (!legalTransaction) {
      return;
    }
    try {
      const uploadObject = getUploadObject();
      const response = await exportPDF(uploadObject);
      const linkSource = `data:application/pdf;base64,${response.pdf}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = response.fileName;
      downloadLink.click();
      onClose();
    } catch (error) {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Exportieren des PDF',
      });
    }
  };

  const handleExportFormat = async (
    format: 'dino' | 'strukturdatensatz' | 'nebenakte' | 'xjustiz'
  ) => {
    try {
      const supportsDocBundle =
        format === 'strukturdatensatz' || format === 'xjustiz';
      const response = await exportFormat({
        idLegalTransaction: legalTransaction.idLegalTransaction,
        format,
        version: format === 'xjustiz' ? xjustizVersion : undefined,
        areaOfLaw:
          format === 'strukturdatensatz' && sdsAreaOfLaw
            ? sdsAreaOfLaw
            : undefined,
        includeDocuments: supportsDocBundle ? includeDocuments : undefined,
      });
      downloadBase64(response);
      const docCount = (response as { meta?: { documentCount?: number } })?.meta
        ?.documentCount;
      if (supportsDocBundle && includeDocuments && docCount && docCount > 0) {
        showAlert({
          severity: 'success',
          text: `Export erstellt — ${docCount} Dokument(e) im ZIP enthalten.`,
        });
      }
      onClose();
    } catch (error) {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Export',
      });
      console.error(error);
    }
  };

  const handleExport = () => {
    if (selectedMenu === 1) {
      handleExportPdf();
    } else if (selectedMenu === 2) {
      handleExportFormat('nebenakte');
    } else if (selectedMenu === 3) {
      handleExportFormat('xjustiz');
    } else if (selectedMenu === 4) {
      handleExportFormat('strukturdatensatz');
    } else if (selectedMenu === 5) {
      handleExportFormat('dino');
    }
  };

  return (
    <>
      <Button variant='outlined' onClick={handleExportButtonClick}>
        Export
      </Button>

      <Dialog
        open={unsavedConfirmOpen}
        onClose={() => !isSavingBeforeExport && setUnsavedConfirmOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Nicht gespeicherte Änderungen</DialogTitle>
        <DialogContent>
          <DialogContentText component='div'>
            <Stack spacing={1.5}>
              <Box>
                Dieser Vorgang hat ungespeicherte Änderungen — zum Beispiel neu
                hochgeladene Dokumente oder bearbeitete Daten.
              </Box>
              <Box>
                Wenn Sie jetzt ohne Speichern exportieren, sind diese Änderungen{' '}
                <b>nicht</b> Teil des Exports.
              </Box>
            </Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => setUnsavedConfirmOpen(false)}
            disabled={isSavingBeforeExport}
          >
            Abbrechen
          </Button>
          <Button
            color='inherit'
            onClick={handleSkipSaveAndContinue}
            disabled={isSavingBeforeExport}
          >
            Ohne Speichern exportieren
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveAndContinue}
            disabled={isSavingBeforeExport}
            startIcon={
              isSavingBeforeExport ? <CircularProgress size={16} /> : undefined
            }
          >
            Speichern und exportieren
          </Button>
        </DialogActions>
      </Dialog>

      {isOpenDialog && (
        <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
          <DialogTitle>Vorgang exportieren</DialogTitle>
          <DialogContent>
            <Typography>
              Wählen Sie aus, in welchem Format der Vorgang exportiert werden
              soll.
            </Typography>
            <TextField
              sx={{ mt: 2 }}
              label='Format'
              value={selectedMenu}
              onChange={event => setSelectedMenu(Number(event.target.value))}
              fullWidth
              select
            >
              <MenuItem key={`export-select-1`} value={1}>
                PDF-Dokument
              </MenuItem>
              <MenuItem key={`export-select-2`} value={2}>
                Digitale Nebenakte
              </MenuItem>
              <MenuItem key={`export-select-3`} value={3}>
                XJustiz
              </MenuItem>
              <MenuItem key={`export-select-4`} value={4}>
                Strukturdatensatz
              </MenuItem>
              <MenuItem key={`export-select-5`} value={5}>
                DiNo-Datensicherung
              </MenuItem>
            </TextField>

            {selectedMenu === 4 && (
              <>
                <Alert severity='info' sx={{ mt: 2 }}>
                  <AlertTitle>Strukturdatensatz</AlertTitle>
                  Standardisiertes Austauschformat (BNotK). Übergibt
                  Vorgangsdaten – Beteiligte, Aktenzeichen, ggf. Grundstücke –
                  an unterstützende Notarsoftware.
                </Alert>
                <TextField
                  sx={{ mt: 2 }}
                  select
                  label='Rechtsgebiet'
                  value={sdsAreaOfLaw}
                  onChange={e => setSdsAreaOfLaw(e.target.value)}
                  fullWidth
                  helperText='Wird automatisch aus der Vorgangsart abgeleitet. Bei Bedarf hier überschreiben.'
                >
                  <MenuItem value=''>
                    <em>Automatisch</em>
                  </MenuItem>
                  {(sdsFormat?.areaOfLawOptions ?? []).map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}

            {selectedMenu === 1 && (
              <Alert severity='info' sx={{ mt: 2 }}>
                Erzeugt eine ausdruckbare Zusammenfassung des Vorgangs als
                PDF-Datei.
              </Alert>
            )}

            {selectedMenu === 2 && (
              <Alert severity='info' sx={{ mt: 2 }}>
                <AlertTitle>Digitale Nebenakte</AlertTitle>
                Vollständige Übergabe inklusive Dokumenten und Beteiligten in
                einem ZIP-Archiv – kompatibel zum BNotK-Standard.
              </Alert>
            )}

            {selectedMenu === 5 && (
              <Alert severity='info' sx={{ mt: 2 }}>
                <AlertTitle>DiNo-Datensicherung</AlertTitle>
                Verlustfreie Sicherung des Vorgangs für den Wiedereinspielen in
                DiNo – etwa bei Übergabe zwischen Installationen.
              </Alert>
            )}

            {selectedMenu === 3 && (
              <>
                <Alert severity='warning' sx={{ mt: 2 }}>
                  <AlertTitle>Hinweis zum XJustiz-Export</AlertTitle>
                  <Box>
                    XJustiz ist nicht für die Übertragung eines vollständigen
                    Vorgangs vorgesehen. Übertragen werden ausschließlich die
                    <b> Beteiligten</b> – nicht Dokumente, Maßnahmen, Termine
                    oder weitere Vorgangsdaten.
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    Die Datei wird als Handelsregister-Mitteilung verpackt,
                    damit gängige Notarprogramme die Beteiligten als Stammdaten
                    einlesen können. Es handelt sich nicht um einen echten
                    Handelsregister-Auszug.
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    Alle Beteiligten werden als <b>Gesellschafter</b> übergeben.
                    Der Datensatz enthält zusätzlich eine Container-Firma mit
                    dem Präfix <b>„NICHT IMPORTIEREN"</b>, in die Vorgangstitel,
                    Vorgangsart, Urkundennummern und das Beurkundungsdatum
                    eingebettet sind. DiNo erkennt diesen Container beim
                    Re-Import und zeigt die Daten direkt im Dialogeingang an.
                  </Box>
                </Alert>
                {xjustizFormat?.versions &&
                  xjustizFormat.versions.length > 1 && (
                    <TextField
                      sx={{ mt: 2 }}
                      select
                      label='XJustiz-Version'
                      value={
                        xjustizVersion ?? xjustizFormat.defaultVersion ?? ''
                      }
                      onChange={e => setXjustizVersion(e.target.value)}
                      fullWidth
                      helperText='Wählen Sie die Version, die die Empfänger-Software unterstützt. Bei Unsicherheit die Voreinstellung beibehalten.'
                    >
                      {xjustizFormat.versions.map(v => (
                        <MenuItem key={v} value={v}>
                          XJustiz {v}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
              </>
            )}

            {(selectedMenu === 3 || selectedMenu === 4) && (
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Checkbox
                    checked={includeDocuments}
                    onChange={e => setIncludeDocuments(e.target.checked)}
                  />
                }
                label={
                  selectedMenu === 4
                    ? 'Urkunden + Entwürfe + sonstige Dokumente im ZIP mitliefern'
                    : 'Urkunden + Entwürfe + sonstige Dokumente als ZIP-Anhang mitliefern'
                }
              />
            )}
            {(selectedMenu === 3 || selectedMenu === 4) && includeDocuments && (
              <Alert severity='info' sx={{ mt: 1 }}>
                Der Export wird als ZIP geliefert: enthält die{' '}
                {selectedMenu === 4 ? 'SDS-JSON-Datei' : 'XJustiz-XML-Datei'}{' '}
                und die im Vorgang hinterlegten Dokumente unter{' '}
                {selectedMenu === 4 ? (
                  <code>files/</code>
                ) : (
                  <code>dokumente/</code>
                )}
                .
              </Alert>
            )}
            {/*
            <FormControlLabel
              control={
                <Checkbox
                  defaultChecked
                  value={saveBeforce}
                  onChange={event => setSaveBeforce(event.target.checked)}
                />
              }
              label='Vorgang vor dem Export sichern.'
            />
             */}
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={onClose} disabled={isPending}>
              Abbruch
            </Button>
            <Button
              variant='contained'
              onClick={handleExport}
              disabled={isPending}
            >
              {isPending ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Wird exportiert …</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Exportieren'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
